import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import useDispatchStore from "@web-app/store/dispatchStore";
import {
  CreateCustomFieldPayload,
  CustomFieldValue,
  UpdateCustomFieldPayload,
} from "@web-app/types";

import {
  create,
  groupList,
  groupListByIds,
  list,
  publicGroupList,
  publicList,
  remove,
  update,
  updateOrderCustomFieldValue,
} from "../services/customFields";

export const useCustomFieldsAndGroups = ({ enabled = true } = {}) => {
  const { user } = useDispatchStore();

  return useQueries({
    queries: [
      {
        enabled,
        queryFn: async () => {
          const response = await list(user?.currentWorkspaceId || "");
          return response;
        },
        queryKey: ["customFields"],
      },
      {
        enabled,
        queryFn: async () => {
          const response = await groupList();
          return response;
        },
        queryKey: ["customFieldGroups"],
      },
    ],
  });
};

export const useActiveCustomFieldsAndGroups = (workspaceId?: string) => {
  const { user } = useDispatchStore();

  // Use provided workspaceId (for public routes) or fall back to user's current workspace
  const effectiveWorkspaceId = workspaceId || user?.currentWorkspaceId || "";

  // Use public endpoints when workspaceId is explicitly provided (public routes)
  // Use authenticated endpoints when using session user's workspace
  const isPublicRoute = !!workspaceId;

  return useQueries({
    queries: [
      {
        queryFn: async () => {
          const response = isPublicRoute
            ? await publicList(effectiveWorkspaceId, true)
            : await list(effectiveWorkspaceId, true);
          return response;
        },
        queryKey: ["customFields", "activeCustomFields", effectiveWorkspaceId],
      },
      {
        queryFn: async () => {
          const response = isPublicRoute
            ? await publicGroupList(effectiveWorkspaceId)
            : await groupList();
          return response;
        },
        queryKey: ["customFieldGroups", effectiveWorkspaceId],
      },
    ],
  });
};

export const useCustomFieldGroupsByIds = (groupIds: string[]) => {
  return useQuery({
    enabled: groupIds.length > 0,
    queryFn: () => groupListByIds(groupIds),
    queryKey: ["customFieldGroups", "byIds", groupIds],
  });
};

export const useCustomFieldMutation = () => {
  const queryClient = useQueryClient();

  const createCustomField = useMutation({
    mutationFn: async (payload: CreateCustomFieldPayload) => {
      const response = await create(payload);
      return response;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customFields"] });
    },
  });

  const updateCustomField = useMutation({
    mutationFn: async ({
      customFieldId,
      payload,
    }: {
      customFieldId: string;
      payload: Partial<UpdateCustomFieldPayload>;
    }) => {
      const response = await update(payload, customFieldId);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customFields"] });
    },
  });

  const updateOrderCustomField = useMutation({
    mutationFn: async ({
      entityId,
      fieldId,
      payload,
    }: {
      entityId: string;
      fieldId: string;
      payload: { customFieldValues: CustomFieldValue[] };
    }) => {
      const response = await updateOrderCustomFieldValue(
        payload,
        entityId,
        fieldId,
      );
      return response;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orderCustomFields"] });
    },
  });

  const deleteCustomField = useMutation({
    mutationFn: async (customFieldId: string) => {
      const response = await remove(customFieldId);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customFields"] });
    },
  });

  return {
    createCustomField,
    deleteCustomField,
    updateCustomField,
    updateOrderCustomField,
  };
};
